/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import {
  Card,
  Alert,
  Typography,
  Link,
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { OrderTracking } from '@one-thd/sui-icons';
import { ALERT_MESSAGES } from '../constants';
import { hasExceededMaxRetries, recordRetryAttempt } from '../utils/retryTracker';

interface ActiveOrdersErrorProps {
  refresh: () => void | Promise<void>;
  componentLocation?: string;
  isDashboardSnapshot?: boolean;
}

export const ActiveOrdersError = ({
  refresh,
  componentLocation = 'default',
  isDashboardSnapshot = false,
}: ActiveOrdersErrorProps) => {
  const [hasExceededRetries, setHasExceededRetries] = useState<boolean>(false);

  useEffect(() => {
    setHasExceededRetries(hasExceededMaxRetries());
  }, []);

  const renderHeader = () => (
    <div className="sui-flex sui-items-start sui-justify-between sui-gap-4 sui-w-full">
      <div className="sui-flex sui-items-center sui-gap-3 sui-min-w-0">
        <span className="sui-flex sui-shrink-0 sui-text-primary">
          <OrderTracking />
        </span>
        <div className="sui-flex sui-flex-wrap sui-items-center sui-gap-x-2 sui-gap-y-1">
          <Typography
            variant="h4"
            weight="display"
            className="sui-leading-none"
          >
            Active Deliveries & Pickups
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <div
      data-component="ActiveOrdersError"
      data-testid="active-orders-error"
      data-component-location={componentLocation || 'default'}
      className="sui-h-full"
    >
      <Card
        className={classNames('sui-h-full', {
          'sui-border-1 sui-border-primary sui-rounded-base': !isDashboardSnapshot,
        })}
        disablePadding
        CardContentProps={{
          className: classNames(
            'sui-flex',
            'sui-flex-col',
            'sui-gap-4',
            'sui-h-full',
            'sui-w-full',
            { 'sui-p-4': !isDashboardSnapshot }
          ),
        }}
      >
        {!isDashboardSnapshot && renderHeader()}
        <div className="sui-h-full [&>*]:sui-h-full">
          <Alert status="error" size="large">
            <div>
              <Typography variant="body-base" weight="bold">
                {ALERT_MESSAGES.ITEMS_IN_PROGRESS_ERROR_TITLE}
              </Typography>
              {hasExceededRetries ? (
                <Typography variant="body-base">
                  {ALERT_MESSAGES.ITEMS_IN_PROGRESS_ERROR_MESSAGE}
                </Typography>
              ) : (
                <Typography variant="body-base">
                  <Link
                    href="#"
                    role="button"
                    onClick={async (event: any) => {
                      event.preventDefault();
                      try {
                        // Record retry attempt before refreshing.
                        recordRetryAttempt();
                        if (hasExceededMaxRetries()) {
                          setHasExceededRetries(true);
                          return;
                        }
                        await refresh();
                      } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error('Error refreshing active orders:', err);
                      }
                    }}
                    data-testid="active-orders-refresh"
                  >
                    Refresh Active Orders
                  </Link>
                  {' to try again.'}
                </Typography>
              )}
            </div>
          </Alert>
        </div>
      </Card>
    </div>
  );
};

ActiveOrdersError.defaultProps = {
  componentLocation: 'default',
  isDashboardSnapshot: false,
};
